import { useCallback, useEffect, useState } from 'react';
import { InvestigationReplayView, prefersReducedMotion } from '@/versions/v1/modules/advisory/AgentInvestigationReplayView';
import { Sparkles } from 'lucide-react';
import { HelpAnchor, Pill, SurfacePagePanel, SurfacePageSectionHeader } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { usePersona } from '@/app/providers';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { useAgentTrace, type AgentTraceState } from '@/versions/v1/shared/useAgentTrace';
import { formatClockShort, formatDayMonth } from '@/versions/v1/shared/clock.derive';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import {
  actionGateRead,
  agentApproverLabel,
  agentModelDisplayName,
  createApprovalRecord,
  formatAgentDuration,
  investigationDisplayState,
  investigationRows,
  notifyApproval,
  plantRoleLabel,
  recommendationFromTrace,
  recommendationEarlyWarning,
  type ActionGateTarpLadder,
  type AgentApprovalRecord,
  type AgentDecisionKind,
} from '@/versions/v1/modules/advisory/AgentInvestigation.transform';
import {
  investigationColumnRead,
  investigationEndClock,
  investigationPlayback,
  investigationRunAvailable,
  investigationRunStatus,
  investigationStepClocks,
  playbackProgress,
  runPlaybackStart,
  runProgressReadout,
  runReadout,
  type InvestigationRun,
  type InvestigationRunStatus,
} from '@/versions/v1/modules/advisory/InvestigationRun.transform';
import type { CaseRecoveryCriterion } from '@/versions/v1/schema/case';

export type AgentInvestigationSectionProps = {
  caseId: string;
  fixture: string;
  /** K15: the workflow's run carries the case code, so the run reads `INV-0001 · ZN-RGH-PH`. */
  caseCode?: string;
  diagnosedAt?: SeriesTime;
  controlPlanLabel?: string;
  onApproved?: (caseId: string, causeId: string, earlyWarning?: string, reason?: string) => void;
  layout?: 'drawer' | 'case-page';
  /** Case page: the trace the page already loaded, so it is fetched once. */
  traceState?: AgentTraceState;
  /** Case page (K13 / A4): the run's TARP ladders and the case's recovery criterion for the action gate. */
  tarpLadders?: readonly ActionGateTarpLadder[];
  recovery?: CaseRecoveryCriterion;
  /** Case page (D19): the session's recorded action approval. */
  actionApproval?: { at: string; by: string };
  /** Case page (K15): the case's investigation runs, recorded and session-started. */
  runs?: readonly InvestigationRun[];
  /** Case page (K15): the run a `?run=` deep link lands on. */
  selectedRunId?: string;
  /**
   * #731: false for the floor seats (Operator, Control room), whose estimate
   * band, action gate and recorded value-estimate step read no US$.
   */
  showMoney?: boolean;
};

/** While a run plays, the column follows the playback clock rather than the case clock. */
type LivePlayback = { runId: string; offsetMs: number; playing: boolean };

export function AgentInvestigationSection({
  caseId,
  fixture,
  caseCode,
  diagnosedAt,
  controlPlanLabel,
  onApproved,
  layout = 'drawer',
  traceState,
  tarpLadders,
  recovery,
  actionApproval,
  runs,
  selectedRunId,
  showMoney = true,
}: AgentInvestigationSectionProps) {
  const ownTrace = useAgentTrace(traceState ? undefined : fixture);
  const state = traceState ?? ownTrace;
  const { persona } = usePersona();
  const { can } = useRole();
  const { state: scenario } = useScenario();
  const advisory = useAdvisory();
  const [live, setLive] = useState<LivePlayback | null>(null);
  const [startedRun, setStartedRun] = useState<InvestigationRun | null>(null);
  const [decisionReason, setDecisionReason] = useState('');
  const [decision, setDecision] = useState<AgentApprovalRecord | null>(null);
  const humanCheck = state.status === 'ready' ? recommendationFromTrace(state.trace).human_check : '';
  useEffect(() => { setDecisionReason(humanCheck); }, [caseId, fixture, humanCheck]);

  // A run belongs to the case clock it started on: scrubbing drops the live
  // playback and the column falls back to what the cursor shows.
  useEffect(() => {
    setLive(null);
  }, [caseId, fixture, scenario.cursor]);

  // Switching case or trace clears the page's decision record.
  useEffect(() => {
    setStartedRun(null);
    setDecisionReason(humanCheck);
    setDecision(null);
  }, [caseId, fixture]);

  useEffect(() => {
    if (state.status !== 'ready') return;
    if (selectedRunId) setLive(null);
  }, [selectedRunId, state.status]);

  const totalMs = state.status === 'ready' ? investigationPlayback(state.trace).totalMs : 0;
  // K15 round 2: a run is only offered once the case has reached Diagnosis — the
  // replay cursor is at or past its `diagnosedAt`, which is the workflow's trigger.
  const runAvailable = investigationRunAvailable({ diagnosedAt, cursor: scenario.cursor });
  // The run is `Running` on every surface while its playback is in progress; the
  // shared runs list reads this id so the landing never calls a playing run `At gate`.
  const liveInProgress = live !== null && live.offsetMs < totalMs;
  const setInvestigationRunPlaying = advisory.setInvestigationRunPlaying;

  useEffect(() => {
    setInvestigationRunPlaying(liveInProgress ? live?.runId ?? null : null);
  }, [liveInProgress, live?.runId, setInvestigationRunPlaying]);

  // The run ticker: one animation frame per paint while a run plays, so pausing
  // freezes where the audience is and replaying restarts from the first step.
  useEffect(() => {
    if (!live?.playing) return undefined;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const delta = now - last;
      last = now;
      setLive((current) => {
        if (!current?.playing) return current;
        const offsetMs = Math.min(totalMs, current.offsetMs + delta);
        return { ...current, offsetMs, playing: offsetMs < totalMs };
      });
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [live?.playing, totalMs]);

  const startRun = useCallback(() => {
    if (!runAvailable || state.status !== 'ready') return;
    const run = advisory.startInvestigationRun({
      existing: runs,
      caseId,
      caseCode: caseCode ?? caseId,
      fixture,
      trace: state.trace,
    });
    // Reduced motion shows the finished trace at once: the whole recording is
    // already on the page, so there is nothing to animate.
    setStartedRun(run);
    setLive(runPlaybackStart(run, prefersReducedMotion()));
  }, [advisory, caseCode, caseId, fixture, runs, runAvailable, state]);

  const togglePlayback = useCallback(() => {
    setLive((current) => {
      if (!current) return current;
      if (current.playing) return { ...current, playing: false };
      return { ...current, offsetMs: current.offsetMs >= totalMs ? 0 : current.offsetMs, playing: true };
    });
  }, [totalMs]);

  const toggleDrawerPlayback = useCallback(() => {
    setLive((current) => {
      if (!current || current.offsetMs >= totalMs) {
        if (!runAvailable || state.status !== 'ready') return current;
        const run = advisory.startInvestigationRun({ existing: runs, caseId, caseCode: caseCode ?? caseId, fixture, trace: state.trace });
        return { runId: run.id, offsetMs: 0, playing: true };
      }
      return { ...current, playing: !current.playing };
    });
  }, [advisory, caseCode, caseId, fixture, runAvailable, runs, state, totalMs]);

  if (state.status !== 'ready') return null;

  const trace = state.trace;
  const rows = investigationRows(trace, showMoney);
  const recommendation = recommendationFromTrace(trace);
  const seat = tarpLadders?.find((ladder) => ladder.ladder_id === recommendation.tarp_ladder_id)
    ?.tiers.find((tier) => tier.tier === recommendation.tarp_tier)?.owner_role;
  const approverSeat = seat ? plantRoleLabel(seat) ?? seat : 'Control metallurgist';
  const canDecide = can('action:approve') && agentApproverLabel(persona) === approverSeat;
  const schedule = investigationPlayback(trace);
  const caseRuns = (runs ?? []).filter((run) => run.caseId === caseId);
  const atRest = investigationColumnRead({
    runs: caseRuns,
    cursor: scenario.cursor,
    approvedAt: actionApproval?.at,
    runId: selectedRunId,
  });
  const atRestRun = atRest.state === 'run' ? atRest.run : null;
  const liveRun = live
    ? caseRuns.find((run) => run.id === live.runId) ?? (startedRun?.id === live.runId ? startedRun : null)
    : null;
  const activeRun = liveRun ?? atRestRun;
  const progress = live ? playbackProgress(schedule, live.offsetMs) : null;
  const visibleCount = progress ? progress.visibleCount : activeRun ? rows.length : 0;
  const playing = live?.playing ?? false;
  const displayState = investigationDisplayState({
    // The run's case-clock start: for the recorded run this is the case's
    // diagnosis time, which is also the workflow's trigger.
    diagnosedAt: activeRun?.startedAt,
    cursor: scenario.cursor,
    visibleCount,
    totalRows: rows.length,
    playing,
  });
  const runStatus: InvestigationRunStatus | undefined = activeRun
    ? displayState === 'replaying'
      ? 'running'
      : investigationRunStatus(activeRun, actionApproval?.at)
    : undefined;
  const stepClocks = activeRun ? investigationStepClocks(activeRun, schedule) : undefined;
  const endClock = activeRun ? investigationEndClock(activeRun) : undefined;
  // One readout (K15 round 2): the column header's. It shows progress while the
  // run plays or is paused mid-run, and the comp's count · duration at rest and
  // once finished. The controls carry no second count.
  const headerReadout = activeRun
    ? liveInProgress
      ? runProgressReadout(visibleCount, activeRun.steps)
      : runReadout(activeRun)
    : null;
  const cursorLabel = formatClockShort(scenario.cursor);
  const decisionAtLabel = decision ? cursorLabel : undefined;
  const investigationTime = diagnosedAt
    ? `${formatDayMonth(diagnosedAt)} ${diagnosedAt.slice(11, 16)}`
    : undefined;

  const commitDecision = (kind: AgentDecisionKind) => {
    if (!canDecide) return;
    const record = createApprovalRecord({
      caseId,
      causeId: recommendation.cause_id,
      kind,
      reason: decisionReason,
      approver: agentApproverLabel(persona),
      at: scenario.cursor,
    });
    if (!record) return;
    setDecision(record);
    setDecisionReason('');
    notifyApproval(kind, onApproved ? (id, cause, warning) => onApproved(id, cause, warning, record.reason) : undefined, caseId, recommendation.cause_id, recommendationEarlyWarning(recommendation));
  };

  const view = (
    <InvestigationReplayView
      trace={trace}
      rows={rows}
      visibleCount={visibleCount}
      recommendation={recommendation}
      decision={decision}
      decisionAtLabel={decisionAtLabel}
      decisionReason={decisionReason}
      decisionEnabled={decisionReason.trim().length >= 3}
      canDecide={canDecide}
      playing={playing}
      cursorLabel={cursorLabel}
      onPlay={layout === 'case-page' ? togglePlayback : toggleDrawerPlayback}
      onDecisionReasonChange={setDecisionReason}
      onDecision={commitDecision}
      compact={layout === 'case-page'}
      showRecommendation={layout === 'case-page' && displayState === 'complete'}
      controlPlanLabel={controlPlanLabel}
      runLabel={layout === 'case-page'
        ? undefined
        : [investigationTime, formatAgentDuration(trace.duration_ms)].filter(Boolean).join(' · ')}
      actionGate={layout === 'case-page'
        ? actionGateRead({ recommendation, tarpLadders, recovery, roleLabel: plantRoleLabel, showMoney })
        : undefined}
      approverLabel={approverSeat}
      actionApproval={layout === 'case-page' ? actionApproval : undefined}
      run={activeRun}
      runStatus={runStatus}
      canRun={runAvailable}
      onRun={startRun}
      onTogglePlayback={togglePlayback}
      stepClocks={stepClocks}
      endClock={endClock}
      showMoney={showMoney}
    />
  );

  if (layout === 'case-page') {
    return (
      <Commentable name="AdvisoryCasePage.Investigation">
        <section className="h-full min-w-0">
          <SurfacePagePanel
            title="Investigation"
            className="h-full"
            actions={(
              <div className="flex items-center gap-1.5">
                <Pill
                  tone="default"
                  className="gap-1 border-[color-mix(in_srgb,var(--color-accent-500)_35%,transparent)] bg-[var(--accent-soft)] px-1.5 text-[10px] text-accent-700 dark:text-accent-300"
                  data-actor="agent"
                >
                  <Sparkles className="size-3" aria-hidden="true" />
                  Agent · {agentModelDisplayName(trace.model)}
                </Pill>
                {headerReadout ? (
                  <span className="whitespace-nowrap tabular-nums text-[10px] text-muted" data-investigation-header-readout>
                    {headerReadout}
                  </span>
                ) : null}
                <HelpAnchor doc="advisory/analysis-run" section="agent-investigation" />
              </div>
            )}
          >
            {view}
          </SurfacePagePanel>
        </section>
      </Commentable>
    );
  }

  return (
    <Commentable name="Drawer.AgentInvestigation">
      <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
        <SurfacePageSectionHeader
          title="Investigation"
          actions={(
            <div className="flex flex-wrap items-center justify-end gap-1">
              <Pill tone="default">{agentModelDisplayName(trace.model)}</Pill>
              <Pill tone="default">{formatAgentDuration(trace.duration_ms)}</Pill>
              <HelpAnchor doc="advisory/analysis-run" section="agent-investigation" />
            </div>
          )}
        />
        {view}
      </section>
    </Commentable>
  );
}
